import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Reveal, SectionHeader } from "./Reveal";
import { useLang } from "@/i18n";

const wx = (id, w, h) =>
  id.startsWith("/")
    ? id
    : `https://static.wixstatic.com/media/${id}/v1/fill/w_${w},h_${h},q_85/${id.split("~mv2")[0]}~mv2.jpg`;

const spans = [
  "lg:col-span-7 aspect-[16/10]",
  "lg:col-span-5 aspect-[4/3]",
  "lg:col-span-5 aspect-[4/3]",
  "lg:col-span-7 aspect-[16/10]",
  "lg:col-span-4 aspect-[3/4]",
  "lg:col-span-4 aspect-[3/4]",
  "lg:col-span-4 aspect-[3/4]",
  "lg:col-span-7 aspect-[16/10]",
  "lg:col-span-5 aspect-[4/3]",
  "lg:col-span-12 aspect-[21/9]",
];

export default function Gallery() {
  const { t } = useLang();
  const [open, setOpen] = useState(null);

  const go = (dir) => {
    setOpen((cur) => (cur + dir + t.galleryItems.length) % t.galleryItems.length);
  };

  return (
    <section
      id="galerie"
      data-testid="gallery-section"
      className="py-24 sm:py-32 px-5 sm:px-10"
    >
      <div className="mx-auto max-w-[1500px]">
        <SectionHeader eyebrow={t.gallery.eyebrow} title={t.gallery.title} />
        <p className="font-mono text-[0.6rem] uppercase tracking-[0.3em] text-sage mt-4">
          {t.gallery.caption}
        </p>

        <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-5 sm:gap-6">
          {t.galleryItems.map((item, i) => (
            <Reveal
              key={i}
              delay={0.05 * (i % 3)}
              className={`col-span-1 ${spans[i]}`}
            >
              <button
                data-testid={`gallery-photo-${i}`}
                onClick={() => setOpen(i)}
                className="group relative block w-full h-full overflow-hidden"
              >
                <img
                  src={wx(item.id, 1200, 900)}
                  alt={item.cap}
                  loading="lazy"
                  style={{ transition: "transform 1.4s cubic-bezier(0.22,1,0.36,1)" }}
                  className="w-full h-full object-cover group-hover:scale-[1.05]"
                />
                <span className="absolute inset-0 bg-night/10 group-hover:bg-night/0 transition-colors duration-700" />
                <span className="absolute bottom-4 left-4 font-mono text-[0.6rem] uppercase tracking-[0.25em] text-bone/90 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                  {item.cap}
                </span>
              </button>
            </Reveal>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {open !== null && (
          <motion.div
            data-testid="gallery-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-[80] bg-night/95 backdrop-blur-md flex items-center justify-center p-5"
            onClick={() => setOpen(null)}
          >
            <motion.img
              key={open}
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              src={wx(t.galleryItems[open].id, 1200, 900)}
              alt={t.galleryItems[open].cap}
              data-testid="gallery-lightbox-image"
              className="max-h-[82vh] max-w-[92vw] object-contain"
              onClick={(e) => e.stopPropagation()}
            />
            <p className="absolute bottom-8 left-1/2 -translate-x-1/2 font-mono text-[0.6rem] uppercase tracking-[0.3em] text-gold">
              {t.galleryItems[open].cap}
            </p>
            <button
              data-testid="gallery-lightbox-prev"
              onClick={(e) => { e.stopPropagation(); go(-1); }}
              className="absolute left-4 sm:left-8 top-1/2 -translate-y-1/2 text-bone/70 hover:text-gold font-mono text-2xl px-3"
              aria-label="Précédent"
            >
              ‹
            </button>
            <button
              data-testid="gallery-lightbox-next"
              onClick={(e) => { e.stopPropagation(); go(1); }}
              className="absolute right-4 sm:right-8 top-1/2 -translate-y-1/2 text-bone/70 hover:text-gold font-mono text-2xl px-3"
              aria-label="Suivant"
            >
              ›
            </button>
            <button
              data-testid="gallery-lightbox-close"
              onClick={() => setOpen(null)}
              className="absolute top-5 right-6 text-bone/70 hover:text-gold font-mono text-xs uppercase tracking-[0.25em]"
            >
              ✕
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
